import { gql } from '@apollo/client';
import IconOperations from '@apollo/icons/default/IconOperations.svg';
import {
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
  Code,
  Heading,
} from '@apollo/orbit';
import React from 'react';

import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import {
  TimeRangeSentence,
  TimeRangeSentenceSchemaDiffValidationConfigFragment,
} from '../../../checksPage/checkWorkflowTaskDetailsPage/operationCheckResults/configDetailsModal/TimeRangeSentence';
import { graphRefToString } from '../../../hooks/useGraphRef';

export const CheckDetailsConfigurationSchemaDiffValidationConfigFragment = gql`
  fragment CheckDetailsConfigurationSchemaDiffValidationConfigFragment on SchemaDiffValidationConfig {
    includedVariants
    excludedClients {
      name
      version
    }
    excludedOperationNames {
      name
    }
    ...TimeRangeSentenceSchemaDiffValidationConfigFragment
  }
  ${TimeRangeSentenceSchemaDiffValidationConfigFragment}
`;

export function OperationConfig({
  graphTitle,
  config,
  createdAt,
  graphId,
  baseVariant = '',
}: {
  graphTitle: string;
  config: GraphQLTypes.CheckDetailsConfigurationSchemaDiffValidationConfigFragment;
  createdAt: string | number;
  graphId: string;
  baseVariant: string | undefined;
}) {
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const excludedOperationNames = config.excludedOperationNames?.filter(
    (o): o is NonNullable<typeof o> => !!o,
  );
  return (
    <AccordionItem>
      <AccordionHeader>
        <AccordionTitle className="flex items-center gap-2">
          <IconOperations /> Operations
        </AccordionTitle>
        <AccordionIcon />
      </AccordionHeader>
      <AccordionPanel className="space-y-4">
        <div>
          <Heading size="lg" className="mb-2">
            Timerange
          </Heading>
          <TimeRangeSentence
            config={config}
            graphTitle={graphTitle}
            createdAt={createdAt}
          />
        </div>

        <div>
          <Heading size="lg" className="mb-2">
            Variants
          </Heading>
          <div className="flex flex-wrap gap-2">
            {config.includedVariants?.map((graphVariant, _i, arr) => {
              const isBaseVariant = graphVariant === '';
              if (isBaseVariant && arr.includes(baseVariant)) {
                return null;
              }
              return (
                <Code key={graphVariant}>
                  {graphRefToString({
                    graphVariant: isBaseVariant ? baseVariant : graphVariant,
                    graphId,
                  })}
                </Code>
              );
            })}
          </div>
        </div>

        {isMinLengthArray(1, excludedOperationNames) && (
          <div>
            <Heading size="lg" className="mb-2">
              Excluded operations names
            </Heading>
            <div className="flex flex-wrap gap-2">
              {excludedOperationNames.map(({ name }) => (
                <Code key={name}>{name}</Code>
              ))}
            </div>
          </div>
        )}

        {isMinLengthArray(1, config.excludedClients) && (
          <div>
            <Heading size="lg" className="mb-2">
              Excluded clients
            </Heading>
            <div className="text-code-sm flex flex-wrap gap-2">
              {config.excludedClients.map((client, i, arr) => (
                <div key={`${client.name}${client.version}`}>
                  {client.name || (
                    <span className="italic">Unidentified clients</span>
                  )}
                  {currentPlan.currentPlan?.clientVersions && (
                    <Code className="ml-2">
                      {client.version === null ? (
                        <span className="italic">All versions</span>
                      ) : (
                        client.version
                      )}
                    </Code>
                  )}
                  {i < arr.length - 1 && `, `}
                </div>
              ))}
            </div>
          </div>
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}
